import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import { useMediaQuery } from '@ifca-root/react-component/src/helpers/PDFFormatter/mediaSize';
import {
  alignSeparateItemTables,
  completeItemTableBorders,
  findFooterBlock,
  applyFooterBreak,
  ANCHOR_FIT_CUSHION,
  findFooterTailAnchor,
  fixHeaderTableLayout,
  getKendoSplitWindowPx,
  getPageContentHeightPx,
  getPreviewSheetHeightPx,
  insertFooterCutMarker,
  scenePendingImages,
} from 'helpers/PDFFormatter/pdfExportConfig';
import {
  useGetCompanyQuery,
  useGetCustomerQuery,
  useGetSubcontractQuery,
  useGetJournalEntryQuery,
} from 'generated/graphql';
import { extractHtmlValue } from './ELetterService';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';

export const ELetterPreview = (props: any) => {
  const {
    previewDetail,
    allpreviewDetail,
    accountType,
    docType,
    submittedByLedger,
    remapData, // Remove manual page control props // page, // pageLength,
    page = 0,
    pageLength = 1,
    htmlTemplate,
    isView,
    threshold,
    // CX-5430: opt-in export pagination (container-relative breaks, bottom-
    // anchored footer, #ItemFooter fallback, thead normalization) - passed
    // by project purchase / POB screens only for now; others keep legacy
    exportPagination = false,
    isPOWithRepeatedHeaders = false,
    pageNumber,
    totalPages,
    approvalLog,
    getGlCostCentre,
    watermarkData,
    approvedClaimData,
    getGlMasterCOA,
  } = props; // Remove height as it's not used in the return block below

  // Commented Ren code as i implemented new ways inside ELetterService.tsx
  // const docStatus =
  //   previewDetail?.purchaseStatus ?? previewDetail?.receiptStatus;

  // const showWatermark =
  //   (docStatus === 'ACTIVE' || docStatus === 'SUBMIT') &&
  //   (docType === 'direct-po' ||
  //     docType === 'direct-po-on-behalf-po' ||
  //     docType === 'purchase-order' ||
  //     docType === 'po-on-behalf' ||
  //     (docType === 'payment' &&
  //       (accountType === 'supplier-account' ||
  //         accountType === 'subcontractor-account'
  //       )
  //     )
  //   );

  const [width /* height */] = useMediaQuery();
  const companyID = localStorage.getItem('companyID');

  const { ID, costAdjustmentID }: any = useParams();

  const {
    loading: companyLoading,
    data: { getCompany } = { getCompany: [] },
  } = useGetCompanyQuery({
    fetchPolicy: 'cache-first',
    variables: { companyID },
  });

  const shouldFetchCustomer =
    accountType !== 'project-purchase' &&
    accountType !== 'project-costing' &&
    !!ID &&
    !!companyID;
  const {
    loading: customerLoading,
    data: { getCustomer } = { getCustomer: [] },
  } = useGetCustomerQuery({
    fetchPolicy: 'network-only',
    variables: { ID, companyID },
    skip: !shouldFetchCustomer,
  });

  const shouldFetchSubcontract =
    accountType !== 'supplier-account' &&
    accountType !== 'project-costing' &&
    !!ID &&
    !!companyID;
  const {
    loading: subcontractLoading,
    data: { getSubcontract } = { getSubcontract: [] },
  } = useGetSubcontractQuery({
    fetchPolicy: 'network-only',
    variables: { companyID },
    skip: !shouldFetchSubcontract,
  }); // Use all data (assuming index 0 contains the full dataset // or that remapData is not used when generating the single long page for PDF)

  const shouldFetchJournalEntry =
    accountType === 'project-costing' &&
    !!(costAdjustmentID || ID) &&
    !!companyID;
  const {
    loading: journalEntryLoading,
    data: { getJournalEntry } = { getJournalEntry: null },
  } = useGetJournalEntryQuery({
    fetchPolicy: 'network-only',
    variables: { ID: costAdjustmentID || ID },
    skip: !shouldFetchJournalEntry,
  });
  // For POs with repeated headers, use the page-specific data
  // Otherwise, use the original logic
  const dataDetails = isPOWithRepeatedHeaders
    ? previewDetail // previewDetail already contains page-specific data
    : remapData
    ? remapData[page]
    : previewDetail;

  const [finalHTML, setFinalHTML] = React.useState('');

  const containerRef = useRef<HTMLDivElement | null>(null);
  // CX-5430: the spacer / trailing-trim / full-sheet math all depend on heights
  // that an image load changes without mutating the DOM, so images are watched
  // once each and re-measured on arrival (the resulting spacer change also
  // wakes the parent paginator's observer, which is wanted)
  const watchedImagesRef = useRef<WeakSet<HTMLImageElement>>(new WeakSet());
  const measureRef = useRef<(() => void) | null>(null);
  const [contentFooterPos, setContentFooterPos] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const measure = () => {
      if (exportPagination) {
        // An error counts as arrived like it does in the paginator - a dead
        // image URL must not leave the page measured at its pre-load height
        scenePendingImages(container).forEach(img => {
          if (watchedImagesRef.current.has(img)) return;
          watchedImagesRef.current.add(img);
          const onArrive = () => measureRef.current?.();
          img.addEventListener('load', onArrive, { once: true });
          img.addEventListener('error', onArrive, { once: true });
        });
      }

      // Undo any full-sheet stretch and A4 boundary marks from the previous
      // pass so everything below measures the template's natural height
      if (exportPagination) {
        container.style.minHeight = '';
        Array.from(
          container.querySelectorAll('[data-full-sheet-stretched]'),
        ).forEach(stretched => {
          (stretched as HTMLElement).style.minHeight = '';
          (stretched as HTMLElement).style.height = '';
        });
        Array.from(container.querySelectorAll('.a4-overflow-mark')).forEach(
          mark => mark.remove(),
        );
        const trimmed = container.querySelector(
          '[data-trailing-trim]',
        ) as HTMLElement | null;
        if (trimmed) {
          trimmed.style.paddingBottom =
            trimmed.getAttribute('data-trailing-trim') || '';
          trimmed.removeAttribute('data-trailing-trim');
        }
      }

      // The template sheet is the TALLEST child - the first child can be a
      // stray lead-in element (e.g. an empty <p> before the sheet div)
      let sheet: HTMLElement | undefined;
      Array.from(container.children).forEach(child => {
        if (child.tagName === 'STYLE' || child.tagName === 'SCRIPT') return;
        const candidate = child as HTMLElement;
        if (!sheet || candidate.offsetHeight > sheet.offsetHeight) {
          sheet = candidate;
        }
      });

      // Page capacity derives from the measured template width (templates are
      // not guaranteed 850px wide - content-box padding widens the root).
      // CX-5430: measure the SHEET, not the container - the hidden export
      // wrapper is max-content so the two match there, but the visible
      // preview container is a plain block filling the screen, which grew the
      // preview page with the browser window instead of the template
      const sheetWidth = sheet
        ? Math.max(sheet.scrollWidth, sheet.offsetWidth)
        : 0;
      const templateWidth =
        sheetWidth > 0
          ? sheetWidth
          : Math.max(container.scrollWidth, container.offsetWidth);
      const pageHeightPx = getPageContentHeightPx(templateWidth);

      // Visible preview only: size every page to exactly one A4 sheet - short
      // pages stretch to a proper sheet, and an over-tall page's white sheet
      // now ENDS at the paper edge so the overflow visibly spills past it
      // instead of silently growing the "paper" (the old min-height-only
      // stretch is why the preview could never show a page the export splits;
      // the hidden export wrapper is untouched). A red rule then marks every
      // height at which the download really starts a new sheet. Applied at
      // the END of the pass, after measuring.
      const applyFullSheet = () => {
        if (!exportPagination || !isView) return;
        const sheetHeightPx = getPreviewSheetHeightPx(templateWidth);
        container.style.minHeight = `${sheetHeightPx}px`;
        let contentBottomPx = 0;
        if (sheet) {
          // natural extent BEFORE this pass's sizing goes back on
          contentBottomPx = sheet.offsetTop + sheet.scrollHeight;
          const sheetStyle = window.getComputedStyle(sheet);
          const extra =
            sheetStyle.boxSizing === 'border-box'
              ? 0
              : (parseFloat(sheetStyle.paddingTop) || 0) +
                (parseFloat(sheetStyle.paddingBottom) || 0) +
                (parseFloat(sheetStyle.borderTopWidth) || 0) +
                (parseFloat(sheetStyle.borderBottomWidth) || 0);
          const sizedHeight = `${Math.max(sheetHeightPx - extra, 0)}px`;
          sheet.style.minHeight = sizedHeight;
          // A page is ONE sheet of paper. Never clip: a sheet that hides its
          // own overflow keeps the stretch-only sizing (legal documents -
          // spilling visibly is wanted, vanishing content is not)
          if (sheetStyle.overflowY === 'visible') {
            sheet.style.height = sizedHeight;
          }
          sheet.setAttribute('data-full-sheet-stretched', 'true');
        }
        // Where the download REALLY cuts: one Kendo split window of content
        // per printed sheet (drawn at the printable-area height, not the
        // paper aspect the sheet is shaped to). Marked only when the page
        // overflows it, so a fitting page keeps a clean sheet.
        const windowPx = getKendoSplitWindowPx(templateWidth);
        if (contentBottomPx > windowPx) {
          container.style.position = 'relative';
          // reserve layout room for the spill so it cannot overlap the
          // pagination controls under the preview
          container.style.minHeight = `${contentBottomPx + 24}px`;
          const doc = container.ownerDocument;
          for (
            let boundary = windowPx;
            boundary < contentBottomPx;
            boundary += windowPx
          ) {
            const rule = doc.createElement('div');
            rule.className = 'a4-overflow-mark';
            rule.setAttribute(
              'style',
              `position:absolute; left:0; right:0; top:${Math.round(
                boundary,
              )}px; border-top:2px dashed #d32f2f; pointer-events:none; z-index:9;`,
            );
            const label = doc.createElement('div');
            label.setAttribute(
              'style',
              'position:absolute; right:0; bottom:2px; background:#d32f2f;' +
                ' color:#fff; font:600 10px/14px sans-serif; padding:1px 6px;' +
                ' border-radius:2px 2px 0 0;',
            );
            label.textContent =
              'A4 sheet ends here - the download starts a new sheet';
            rule.appendChild(label);
            container.appendChild(rule);
          }
          const shade = doc.createElement('div');
          shade.className = 'a4-overflow-mark';
          shade.setAttribute(
            'style',
            `position:absolute; left:0; right:0; top:${windowPx}px; height:${Math.max(
              0,
              contentBottomPx - windowPx,
            )}px; background:rgba(211,47,47,0.05); border-left:3px solid #d32f2f; pointer-events:none; z-index:8;`,
          );
          container.appendChild(shade);
        }
      };

      // Company templates are free-form: some only have #ItemFooter (totals
      // + signatory in one block) and some nest #PageFooter inside
      // #ContentFooter - anchor the outermost footer element so the block
      // stays together on the page
      const el = exportPagination
        ? findFooterBlock(container)
        : (container.querySelector('#PageFooter') as HTMLElement) ||
          (container.querySelector('#ContentFooter') as HTMLElement);
      if (!el) {
        setContentFooterPos(null);
        applyFullSheet();
        return;
      }
      // Reset any previous bottom-anchor spacer so measuring again always
      // starts from the same clean state
      const existingSpacer = container.querySelector(
        '.footer-bottom-spacer',
      ) as HTMLElement | null;
      if (existingSpacer) {
        existingSpacer.style.height = '0px';
        existingSpacer.innerHTML = '';
      }

      // Measure relative to this page's own container - absolute document
      // coordinates inflate for the stacked pages inside the hidden export
      // wrapper and caused spurious page breaks on later pages. Divide by the
      // container's visual zoom so the transform-scaled mobile preview
      // measures the same as the unscaled export wrapper.
      const rect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const zoom =
        container.offsetWidth > 0
          ? containerRect.width / container.offsetWidth
          : 1;
      // Legacy screens keep the historical absolute-coordinate measure
      const top = exportPagination
        ? (rect.top - containerRect.top) / zoom
        : rect.top + window.scrollY;
      setContentFooterPos({
        top,
        left: (rect.left - containerRect.left) / zoom,
        width: rect.width,
        height: rect.height,
      });

      const nonBreaking = container.querySelector('.mce-nbsp-wrap');
      const containerCheck = containerRef.current;

      // Manual <!-- pagebreak --> markers; markers inside the footer block
      // are not turned into break divs here - on the purchase print screen
      // they split the footer into logical pages instead (footerBeforeBreak /
      // footerAfterBreakPage in ReturnHTML), and elsewhere they stay inert
      const marker = Array.from(
        containerCheck?.querySelectorAll('strong, p') || [],
      ).find(
        candidate =>
          !el.contains(candidate) &&
          Array.from(candidate.childNodes).some(
            node =>
              node.nodeType === Node.COMMENT_NODE &&
              node.textContent?.trim() === 'pagebreak',
          ),
      );

      // Auto-insert a page break before the footer if it falls beyond threshold.
      // Default is one A4 page of content at the export scale so the preview
      // breaks where the exported PDF actually paginates; forms can still
      // pass their own threshold (RFQ passes Infinity to disable).
      const THRESHOLD =
        typeof threshold === 'number'
          ? threshold
          : exportPagination
          ? pageHeightPx
          : 2917;
      const existingBreak = container.querySelector('.auto-page-break');
      if (top > THRESHOLD && !marker && !nonBreaking) {
        if (!existingBreak) {
          const br = document.createElement('div');
          br.className = 'auto-page-break page-break';
          br.setAttribute(
            'style',
            'page-break-after: always; break-after: page; height: 0; margin: 0; padding: 0;',
          );
          br.setAttribute('data-auto-break', 'true');
          // Insert the break immediately before the footer element
          if (el.parentNode) el.parentNode.insertBefore(br, el);
          // Re-measure after insertion to update positions
          requestAnimationFrame(measure);
        }
      } else if (existingBreak && !marker && !nonBreaking) {
        existingBreak.remove();
      } else if (marker && !nonBreaking) {
        const existingPageBreak = container.querySelector('.page-break');

        if (existingPageBreak) {
          existingPageBreak?.remove();
        }

        const breakEl = document.createElement('div');
        breakEl.className = 'page-break';
        breakEl.style.pageBreakAfter = 'always';
        breakEl.style.breakAfter = 'page';
        breakEl.style.height = '0';

        marker.parentNode?.insertBefore(breakEl, marker);
      } else if (nonBreaking) {
        existingBreak?.remove();
      }

      // Anchor the footer/signatory block to the bottom of its printed page
      // instead of letting it float mid-page after the last item
      if (!exportPagination) return;
      // Pages made by a designer's footer break flow top-down (anchoring the
      // whole footer block opened page-tall gaps on the YESB retests: items
      // vs the TOTAL row before the marker, header vs the notes after it),
      // but the signatory still belongs at the page bottom - so the
      // after-break page anchors its TAIL: the last content-bearing table in
      // the footer block (the signatory box) plus whatever follows it (e.g.
      // a banner). The before-break page's tail is the totals, which hug the
      // items, so it carries no anchor at all.
      let anchorEl: HTMLElement | null = null;
      if (!dataDetails?.footerBeforeBreak) {
        // CX-5430: pin the trailing SIGNATURE CLUSTER, not the whole footer
        // block - the whole-block anchor parked the slack right after the
        // last item row (a random gap above the totals), while straddle-cut
        // pages had none. Totals and remarks now flow with the content on
        // every page shape and only the signature region (content below the
        // footer's last hr, or its signatory table) pins to the page
        // bottom. The before-break page still carries no anchor at all -
        // its tail is the totals, which hug the items.
        anchorEl = findFooterTailAnchor(el);
        // an after-break footer with no separator flows instead: anchoring
        // the whole block there is what opened the page-tall gap between
        // the repeated header and the notes on the YESB retests
        if (anchorEl === el && dataDetails?.footerAfterBreakPage) {
          anchorEl = null;
        }
      }
      if (!anchorEl) {
        applyFullSheet();
        return;
      }
      const footerRect = anchorEl.getBoundingClientRect();
      const measuredRect = container.getBoundingClientRect();
      const contentBottom = (footerRect.bottom - measuredRect.top) / zoom;
      // Whatever renders below the footer block (trailing elements, the
      // sheet's bottom padding) must fit on the page too - ignoring it let
      // the export push the footer onto an extra page that the preview and
      // the [TotalPage] count never showed
      const heightAfterFooter = Math.max(
        0,
        (measuredRect.bottom - footerRect.bottom) / zoom,
      );
      let pageBottom = contentBottom + heightAfterFooter;
      let pageCount = Math.max(1, Math.ceil(pageBottom / pageHeightPx));
      // CX-5430: trim trailing whitespace instead of adding a page
      if (pageCount > 1) {
        const overflow = pageBottom - (pageCount - 1) * pageHeightPx;
        let sheet: HTMLElement | null = el;
        while (sheet && sheet.parentElement !== container) {
          sheet = sheet.parentElement;
        }
        const padBottom = sheet
          ? parseFloat(window.getComputedStyle(sheet).paddingBottom) || 0
          : 0;
        const available = Math.min(padBottom, heightAfterFooter);
        if (sheet && overflow <= 32 && available >= overflow + 8) {
          const trim = Math.min(available, overflow + 16);
          if (!sheet.hasAttribute('data-trailing-trim')) {
            sheet.setAttribute('data-trailing-trim', sheet.style.paddingBottom);
          }
          sheet.style.paddingBottom = `${padBottom - trim}px`;
          pageBottom -= trim;
          pageCount -= 1;
        }
      }
      // CX-5430: a page that STILL needs more than one window has no bottom to
      // anchor to. Anchoring to the bottom of the second window manufactures a
      // blank sheet - the header prints alone and the whole footer lands on
      // the sheet after it (seen on a TMS PO whose footer block alone is
      // taller than the space left). Leave it unanchored and let it flow; the
      // cure for such a footer is splitting it, not padding it.
      // 16px safety so rounding never spills an extra blank page; the
      // cushion keeps the anchored bottom clear of Kendo's taller re-layout
      const gap =
        pageCount > 1
          ? 0
          : Math.floor(pageHeightPx * ANCHOR_FIT_CUSHION - pageBottom - 16);
      if (gap > 0 && anchorEl.parentNode) {
        let spacerEl = existingSpacer;
        if (!spacerEl) {
          spacerEl = document.createElement('div');
          spacerEl.className = 'footer-bottom-spacer';
        }
        // insert every pass - the spacer must MOVE when the anchor element
        // changes (whole footer block vs the after-break page's tail table)
        anchorEl.parentNode.insertBefore(spacerEl, anchorEl);
        // Kendo's DOM paginator cannot split a childless div: a page boundary
        // landing inside a solid-height spacer shoved the whole footer
        // (page-break-inside: avoid) to the TOP of the next page. Fill the
        // gap with thin strips it can break between instead.
        const STRIP_PX = 8;
        const stripCount = Math.floor(gap / STRIP_PX);
        const remainderPx = gap - stripCount * STRIP_PX;
        const strip = (h: number) =>
          `<div style="height:${h}px; margin:0; padding:0; font-size:0; line-height:0;"></div>`;
        spacerEl.style.height = '';
        spacerEl.innerHTML =
          (remainderPx > 0 ? strip(remainderPx) : '') +
          strip(STRIP_PX).repeat(stripCount);
      }
      applyFullSheet();
    };

    measureRef.current = measure;
    measure();

    let ro: ResizeObserver | null = null;
    if ((window as any).ResizeObserver) {
      ro = new (window as any).ResizeObserver(measure);
      const footerEl = exportPagination
        ? findFooterBlock(container)
        : (container.querySelector('#PageFooter') as Element) ||
          (container.querySelector('#ContentFooter') as Element);
      if (footerEl) ro.observe(footerEl);
    }

    window.addEventListener('resize', measure);
    return () => {
      measureRef.current = null;
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, [finalHTML, width]);

  // useEffect(() => {
  //   if (contentFooterPos) {
  //     // Might need this later on. Dont remove yet.
  //     // Call parent callback if provided
  //     // onFooterMeasure(contentFooterPos);
  //   }
  // }, [contentFooterPos]);

  const ReturnHTML = () => {
    let htmlContent;

    // Prepare data with page information for the template
    const templateData = {
      ...dataDetails,
      // Ensure page number and total pages are available
      PageNo: pageNumber || page + 1,
      TotalPage: totalPages || pageLength,
    };

    htmlContent = extractHtmlValue(
      htmlTemplate,
      accountType,
      docType,
      previewDetail,
      allpreviewDetail,
      getCompany,
      getJournalEntry,
      getCustomer,
      getSubcontract, // Pass placeholder/remove page and pageLength params // if extractHtmlValue is now updated to return all content.
      isPOWithRepeatedHeaders ? 0 : page, // For POs with repeated headers, always use page 0
      totalPages || pageLength,
      submittedByLedger,
      threshold,
      approvalLog,
      getGlCostCentre,
      watermarkData,
      approvedClaimData,
      getGlMasterCOA,
    );

    let parser = new DOMParser();
    let doc = parser.parseFromString(
      // Remove the fixed height CSS that locks it to one page.
      htmlContent.replace('height: 297mm', ''),
      'text/html',
    ); // START: CRITICAL CSS INJECTION FOR HORIZONTAL FIT // This forces tables inside the HTML to be responsive and prevent overflow

    const style = doc.createElement('style');
    const isPurchaseOrder =
      docType.includes('purchase-order') ||
      docType.includes('direct-po') ||
      docType.includes('direct-po-on-behalf') ||
      docType.includes('po-on-behalf');

    const currentPage = pageNumber || page + 1;
    const totalPagesValue = totalPages || pageLength;
    const isLastPage = currentPage === totalPagesValue;
    // A page carrying the part of the footer BEFORE a manual page break
    // (totals) is not the last page but still renders its footer part
    const rendersFooter = isLastPage || dataDetails?.footerBeforeBreak === true;
    if (!rendersFooter) {
      const contentFooter = doc.querySelector('#ContentFooter');
      const itemFooter = doc.querySelector('#ItemFooter');

      if (contentFooter) contentFooter.remove();
      if (itemFooter) itemFooter.remove();

      if (!doc.querySelector('.page-continuation')) {
        const continuationMsg = doc.createElement('div');
        continuationMsg.className = 'page-continuation';
        continuationMsg.textContent = '*Continued on the next page*';
        continuationMsg.setAttribute(
          'style',
          'text-align:center; font-style:italic; color:#666; margin:12px 0;',
        );

        const body = doc.querySelector('body') || doc.documentElement;

        const firstFooter =
          doc.querySelector('#ContentFooter') ||
          doc.querySelector('#ItemFooter');
        if (firstFooter && firstFooter.parentNode) {
          firstFooter.parentNode.insertBefore(continuationMsg, firstFooter);
        } else {
          body.appendChild(continuationMsg);
        }
      }
    }

    const shouldHideFooter = isPurchaseOrder && !rendersFooter;

    if (shouldHideFooter) {
      style.textContent += `
        #ContentFooter,
        #ItemFooter {
          display: none !important;
          visibility: hidden !important;
          height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
        }
      `;
    } else {
      style.textContent += `
        #ContentFooter,
        #ItemFooter {
          display: block !important;
          visibility: visible !important;
          height: auto !important;
        }
      `;
    }

    let existingStyle = style.textContent || '';
    // CX-5430: the export pagination path honours the template's own
    // table-layout and column widths - forcing auto layout (and a 150px
    // 3rd column, which is a DIFFERENT column in the header vs item
    // tables) let content resize columns and misalign the two tables
    style.textContent =
      existingStyle +
      `
 /* PREVIEW CSS - Maintain original fixed width */

 /* Keep original table styling */
 table {
   ${exportPagination ? '' : 'table-layout: auto !important;'}
   width: 100% !important;
 }

 /* Ensure text wrapping but keep reasonable padding */
 td, th {
   word-break: break-word !important;
   overflow-wrap: break-word !important;
   padding: 6px 4px !important;
 }

 ${
   exportPagination
     ? ''
     : `/* Relax the column constraints for preview */
 td:nth-child(3), th:nth-child(3) {
   max-width: none !important;
   min-width: 150px !important;
 }`
 }

 /* CRITICAL: Force the original 850px fixed width */
 #pdf-data-print {
   width: 850px !important; /* Force the original template width */
   max-width: 850px !important;
   min-width: 850px !important;
   overflow: visible !important;
   margin: 0 auto !important; /* Center it */
 }
    #pdf-data-print > div {
   max-width: none !important; /* Remove A4 constraint */
   width: auto !important; /* Natural width */
 /* PDF-SPECIFIC STYLES FOR HEADER REPETITION */
    @media print {
      /* Define running elements for header and footer */
      .header {
        position: running(header);
      }
      
      .footer {
        position: running(footer);
      }

      /* Page margins with space for headers and footers */
      @page {
        margin-top: 100px;
        margin-bottom: 50px;
        
        /* Insert running headers and footers */
        @top-center {
          content: element(header);
        }
        
        @bottom-center {
          content: element(footer);
        }
      }

      /* Ensure page breaks work correctly */
      .page-break {
        page-break-after: always;
        break-after: page;
      }

       /* Make sure each PO page starts on a new page */
       [data-is-po="true"] {
    page-break-before: always;
  }
}

      /* Reset body margins for print */
      body {
        margin: 0;
        padding: 0;
      }

      /* Ensure the main content doesn't overlap with headers/footers */
      #pdf-data-print {
        margin-top: 0 !important;
      }
    }

    /* Alternative approach for browsers that don't support running() */
    .repeat-header {
      display: none;
    }

    @media print {
      .repeat-header {
        display: block;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        background: white;
        z-index: 9999;
      }
      
      /* Add top margin to content to avoid overlap */
      body {
        margin-top: 100px;
      }
    }
  `;
    const head = doc.querySelector('head');
    if (head) {
      head.appendChild(style);
    }

    // If your HTML template has a header section, add running class to it
    const headerElements = doc.querySelectorAll('.header, thead, .page-header');
    headerElements.forEach(header => {
      header.classList.add('header');
    });

    // If you have footer elements
    const footerElements = doc.querySelectorAll('.footer, tfoot, .page-footer');
    footerElements.forEach(footer => {
      footer.classList.add('footer');
    });

    // Footer-only trailer page (footerOnOwnPage): drop the empty item table
    // (incl. its column headers) unless the template keeps its footer inside
    // that table - then only the raw #ItemValue row is dropped
    if (dataDetails?.footerOnlyPage) {
      const footerIds = '#PageFooter, #ContentFooter, #ItemFooter';
      const itemValue = doc.querySelector('#ItemValue');
      const itemBlock = itemValue?.closest('table') ?? itemValue;
      if (itemBlock && !itemBlock.querySelector(footerIds)) {
        itemBlock.remove();
      } else {
        itemValue?.remove();
      }
      const itemLabel = doc.querySelector('#ItemLabel');
      const labelBlock = itemLabel?.closest('table') ?? itemLabel;
      if (labelBlock && !labelBlock.querySelector(footerIds)) {
        labelBlock.remove();
      }
    }

    // CX-5430: manual <!-- pagebreak --> in the footer region - the page
    // before the break keeps only the content above the marker (totals), and
    // the content from the marker onward (notes, signatory) renders on its
    // own page with the document header repeated. Split at the marker's own
    // position so any nesting the designer produced is honoured.
    if (dataDetails?.footerBeforeBreak || dataDetails?.footerAfterBreakPage) {
      // An AUTOMATIC cut has no marker in the template body - the paginator
      // measured the boundary that fills the page (an element child of the
      // footer block, or a line break inside one), so put a marker there and
      // the split below is identical to a designer's
      if (dataDetails?.autoFooterCutAt != null) {
        insertFooterCutMarker(doc.body, dataDetails.autoFooterCutAt);
      }
      applyFooterBreak(
        doc.body,
        dataDetails?.footerBeforeBreak ? 'before' : 'after',
      );
    }

    // Ensure the item table has a real <thead> - TinyMCE templates often put
    // the column-header row in <tbody>, and Kendo repeatHeaders only repeats
    // <thead> rows across page splits.
    // The rows preceding the #ItemValue container are the column headers by
    // construction (#ItemValue is the repeated data-row template).
    const itemAnchor = doc.querySelector('#ItemValue');
    const itemTable = itemAnchor?.closest('table');
    if (
      exportPagination &&
      itemAnchor &&
      itemTable &&
      !itemTable.querySelector('thead')
    ) {
      const rows = Array.from(itemTable.querySelectorAll('tr'));
      const headerRows = rows.filter(
        row =>
          !row.contains(itemAnchor) &&
          !itemAnchor.contains(row) &&
          (row.compareDocumentPosition(itemAnchor) &
            Node.DOCUMENT_POSITION_FOLLOWING) !==
            0,
      );
      if (headerRows.length > 0 && headerRows.length <= 3) {
        const thead = doc.createElement('thead');
        headerRows.forEach(row => thead.appendChild(row));
        itemTable.insertBefore(thead, itemTable.firstChild);
      }
    }

    // Separate #ItemLabel / #ItemValue tables (IAQ shape): sync the item
    // tables' column widths to the header table and complete half-declared
    // cell borders that Kendo's PDF writer would draw as dark defaults
    if (exportPagination) {
      // CX-5606: pin header column widths so Kendo lays the header out like the preview
      fixHeaderTableLayout(doc.body);
      alignSeparateItemTables(doc.body);
      completeItemTableBorders(doc.body);
    }

    htmlContent = doc.documentElement.outerHTML;

    return htmlContent;
  };

  React.useEffect(() => {
    const loadHTML = async () => {
      const html = await ReturnHTML();
      setFinalHTML(html);
    };

    if (
      !companyLoading &&
      !customerLoading &&
      !subcontractLoading &&
      !journalEntryLoading
    ) {
      loadHTML();
    }
  }, [
    companyLoading,
    customerLoading,
    subcontractLoading,
    journalEntryLoading,
    dataDetails,
    previewDetail,
    allpreviewDetail,
    htmlTemplate,
    getCompany,
    page,
    pageLength,
    getJournalEntry,
    isPOWithRepeatedHeaders,
    pageNumber,
    totalPages,
    approvalLog,
    getGlCostCentre,
    getGlMasterCOA,
  ]);

  const isPurchaseOrderType =
    docType.includes('purchase-order') ||
    docType.includes('direct-po') ||
    docType.includes('direct-po-on-behalf') ||
    docType.includes('po-on-behalf');

  const currentPageNo = pageNumber || page + 1;
  const totalPageCount = totalPages || pageLength;
  const isLastPage = currentPageNo === totalPageCount;

  return (
    <>
      {companyLoading && <Loading />}
      {customerLoading && <Loading />}
      {subcontractLoading && <Loading />}
      {journalEntryLoading && <Loading />}

      <div style={{ position: 'relative', minHeight: '297mm' }}>
        {/* Implement new ways inside ELetterService.tsx (CX-2825-3717) */}
        {/* {showWatermark && (
          <div
            style={{
              position: 'absolute',
              top: '40%',
              left: '50%',
              transform: 'translate(-50%, -50%) rotate(-30deg)',
              fontSize: '120px',
              fontWeight: 'bold',
              color: 'rgba(0, 0, 0, 0.12)',
              zIndex: 999999,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            DRAFT
          </div>
        )} */}

        <div
          ref={containerRef}
          id={`pdf-data-print-${docType}`}
          data-claim={docType}
          data-is-po={isPurchaseOrderType ? 'true' : 'false'}
          data-page={pageNumber || page + 1}
          data-total-pages={totalPages || pageLength}
          style={
            width < 769 && isView === true
              ? {
                  width: `210mm`,
                  height: 'auto',
                  minHeight: '297mm',
                  transform: `scale(${Math.min(width / 800, 0.9)})`,
                  transformOrigin: 'top left',
                  overflow: 'visible',
                }
              : width < 769 && isPurchaseOrderType && isView
              ? {
                  width: `210mm`,
                  height: 'auto',
                  minHeight: '297mm',
                  transform: `scale(${Math.min(width / 800, 0.9)})`,
                  transformOrigin: 'top left',
                  overflow: 'visible',
                }
              : {}
          }
          dangerouslySetInnerHTML={{ __html: finalHTML }}
        />
      </div>
    </>
  );
};

export default ELetterPreview;
